import { useEffect, useMemo, useRef, type ReactNode } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Text } from "@similarweb/ui-components/foundations";

import { TableLoader } from "app/components/React/Table/FlexTable/Big/FlexTableStatelessComponents";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";

import { GROWTH_OVERVIEW_QUERY_KEY, GROWTH_OVERVIEW_COLUMN_WIDTHS } from "../const";
import { useGrowthOverviewContext } from "../context/GrowthOverviewProvider";
import {
    GROWTH_OVERVIEW_I18N_KEYS,
    getGrowthOverviewColumnNameKey,
    getGrowthOverviewShowMoreDomainsLabel,
    getGrowthOverviewShowMoreIndustriesLabel,
    getGrowthOverviewShowMoreSubIndustriesLabel,
    getGrowthOverviewSummaryColumnKey,
} from "../growthOverviewTexts";
import { useGrowthOverviewRowHover } from "../hooks/useGrowthOverviewRowHover";
import { useGrowthOverviewTableRows } from "../hooks/useGrowthOverviewTableRows";
import {
    useGrowthOverviewHeatmap,
    useGrowthOverviewRowExpansion,
    useGrowthOverviewTableSort,
} from "../hooks/useGrowthOverviewTableState";
import type { IGrowthOverviewTableRow } from "../types";
import { GROWTH_OVERVIEW_NAME_SORT_FIELD, GROWTH_OVERVIEW_SUMMARY_SORT_FIELD, GrowthOverviewLevel } from "../types";
import { findGrowthOverviewRowById } from "../utils/findGrowthOverviewRowById";
import { getVisibleDomainChildren } from "../utils/getVisibleDomainChildren";
import { isGrowthOverviewRowExpandable } from "../utils/isGrowthOverviewRowExpandable";
import { collectUnionPeriodHeaders } from "../utils/mapApiRows";
import { sortTableRows } from "../utils/sortTableRows";

import {
    type IGrowthOverviewPeriodCellParams,
    renderBranchLoadingRowCells,
    renderDomainRowCells,
    renderGroupRowCells,
    renderShowAllDomainsRowCells,
} from "./GrowthOverviewGridRows";
import { GrowthOverviewHeatmapProvider } from "./GrowthOverviewHeatmapContext";
import { GrowthOverviewSortHeader } from "./GrowthOverviewSortHeader";
import {
    GrowthOverviewTableScrollSteppers,
    GrowthOverviewTableScrollWrapper,
} from "./GrowthOverviewTableScrollSteppers";
import { GrowthOverviewTableTop } from "./GrowthOverviewTableTop";
import {
    GrowthOverviewGrid,
    GrowthOverviewNoData,
    GrowthOverviewTableContainer,
    GrowthOverviewTableLoader,
    GrowthOverviewTableScrollArea,
} from "./styled";

const { name: NAME_WIDTH } = GROWTH_OVERVIEW_COLUMN_WIDTHS;

type TPendingRevealScroll = { parentId?: string; index: number };

// index is the pre-fetch offset in API order; scroll targets that row by id regardless of sort order.
const getRevealedRowId = (rows: IGrowthOverviewTableRow[], pending: TPendingRevealScroll): string | undefined =>
    pending.parentId
        ? findGrowthOverviewRowById(rows, pending.parentId)?.children?.[pending.index]?.id
        : rows[pending.index]?.id;

const scrollToRevealedRow = (scrollArea: HTMLElement, rowId: string): void => {
    scrollArea
        .querySelector(`[data-automation="${GROWTH_OVERVIEW_QUERY_KEY}-row-name-${rowId}"]`)
        ?.scrollIntoView({ block: "start", inline: "nearest", behavior: "smooth" });
};

interface IRenderRowBranchParams {
    row: IGrowthOverviewTableRow;
    nestDepth: number;
    expandedRowKeys: ReadonlySet<string>;
    showAllDomainsRowKeys: ReadonlySet<string>;
    loadingRowIds: ReadonlySet<string>;
    onToggleRow: (row: IGrowthOverviewTableRow) => void;
    onShowAllDomains: (rowId: string) => void;
    onShowMoreSubIndustries: (rowId: string) => void;
    periodCellParams: Omit<IGrowthOverviewPeriodCellParams, "row" | "showPeriodHeatmap" | "highlighted">;
    getRowHoverProps: ReturnType<typeof useGrowthOverviewRowHover>["getRowHoverProps"];
}

const renderRowBranch = ({
    row,
    nestDepth,
    expandedRowKeys,
    showAllDomainsRowKeys,
    loadingRowIds,
    onToggleRow,
    onShowAllDomains,
    onShowMoreSubIndustries,
    getRowHoverProps,
    periodCellParams,
}: IRenderRowBranchParams): ReactNode[] => {
    const rowHover = getRowHoverProps(row.id);

    if (row.rowType === "domain") {
        return renderDomainRowCells({ ...periodCellParams, row, nestDepth, rowHover });
    }

    const isExpanded = expandedRowKeys.has(row.id);
    const isLoadingBranch = loadingRowIds.has(row.id);
    const groupCells = renderGroupRowCells({
        ...periodCellParams,
        row,
        nestDepth,
        isExpanded,
        isLoadingBranch,
        onToggle: onToggleRow,
        rowHover,
    });

    if (!isExpanded || !isGrowthOverviewRowExpandable(row)) {
        return groupCells;
    }

    const loadingRowCells = isLoadingBranch
        ? renderBranchLoadingRowCells({
              rowId: row.id,
              nestDepth: nestDepth + 1,
              periodHeaders: periodCellParams.periodHeaders,
          })
        : [];

    if (!row.children?.length) {
        return isLoadingBranch ? [...groupCells, ...loadingRowCells] : groupCells;
    }

    if (row.rowType === "industry") {
        const loadedSubIndustryCount = row.children?.length ?? 0;
        const totalSubIndustryCount = row.totalSubIndustryCount ?? loadedSubIndustryCount;
        const remainingSubIndustryCount = Math.max(totalSubIndustryCount - loadedSubIndustryCount, 0);
        const showMoreSubIndustryCells =
            remainingSubIndustryCount > 0 && !isLoadingBranch
                ? renderShowAllDomainsRowCells({
                      rowId: `${row.id}-show-more-sub-industries`,
                      periodHeaders: periodCellParams.periodHeaders,
                      label: getGrowthOverviewShowMoreSubIndustriesLabel(remainingSubIndustryCount),
                      nestDepth: nestDepth + 1,
                      onShowAll: () => onShowMoreSubIndustries(row.id),
                      rowHover: getRowHoverProps(`${row.id}-show-more-sub-industries`),
                  })
                : [];

        return [
            ...groupCells,
            ...row.children.flatMap((child) =>
                renderRowBranch({
                    row: child,
                    nestDepth: nestDepth + 1,
                    expandedRowKeys,
                    showAllDomainsRowKeys,
                    loadingRowIds,
                    onToggleRow,
                    onShowAllDomains,
                    onShowMoreSubIndustries,
                    getRowHoverProps,
                    periodCellParams,
                }),
            ),
            ...showMoreSubIndustryCells,
            ...loadingRowCells,
        ];
    }

    const { visibleChildren, showShowAllRow, remainingCount } = getVisibleDomainChildren(
        row.children,
        showAllDomainsRowKeys.has(row.id),
        row.totalDomainCount ?? row.domainCount,
    );
    const childCells = visibleChildren.flatMap((child) =>
        renderRowBranch({
            row: child,
            nestDepth: nestDepth + 1,
            expandedRowKeys,
            showAllDomainsRowKeys,
            loadingRowIds,
            onToggleRow,
            onShowAllDomains,
            onShowMoreSubIndustries,
            getRowHoverProps,
            periodCellParams,
        }),
    );
    const showAllCells =
        showShowAllRow && row.rowType === "sub-industry"
            ? renderShowAllDomainsRowCells({
                  rowId: row.id,
                  periodHeaders: periodCellParams.periodHeaders,
                  label: getGrowthOverviewShowMoreDomainsLabel(remainingCount),
                  nestDepth: nestDepth + 1,
                  onShowAll: () => onShowAllDomains(row.id),
                  rowHover: getRowHoverProps(`${row.id}-show-all`),
              })
            : [];

    return [...groupCells, ...childCells, ...showAllCells, ...loadingRowCells];
};

export const GrowthOverviewTable = () => {
    const { params } = useCurrentStateAndParams();
    const { category } = params;
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const { metric, granularity, searchTerm, debouncedSearchTerm, setSearchTerm } = useGrowthOverviewContext();

    const {
        rows: rawRows,
        responseLevel,
        totalCount: responseTotalCount,
        isLoading,
        isError,
        isFetching,
        isAwaitingRootData,
        loadingRowIds,
        isLoadingMoreIndustries,
        isLoadingMoreSubIndustries,
        loadBranch,
        loadMoreIndustries,
        loadMoreSubIndustries,
    } = useGrowthOverviewTableRows();
    const { sort, handleSort } = useGrowthOverviewTableSort(granularity, metric);
    const {
        expandedRowKeys,
        showAllDomainsRowKeys,
        handleToggleRow,
        handleShowAllDomains,
        handleShowMoreSubIndustries,
    } = useGrowthOverviewRowExpansion(rawRows, rawRows, responseLevel, category, loadBranch, loadingRowIds);
    const gridRef = useRef<HTMLDivElement>(null);
    const { handleRowMouseLeave, getRowHoverProps } = useGrowthOverviewRowHover(gridRef);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const pendingScrollRef = useRef<TPendingRevealScroll | null>(null);

    const rows = useMemo(() => sortTableRows(rawRows, metric, granularity, sort), [rawRows, metric, granularity, sort]);
    const { getHeatmapStyle, getHeatmapColor } = useGrowthOverviewHeatmap(rawRows, metric);

    const periodHeaders = useMemo(() => collectUnionPeriodHeaders(rows, metric), [rows, metric]);
    const periodHeaderKey = useMemo(() => periodHeaders.map((period) => period.key).join("|"), [periodHeaders]);

    const topLevelExpandableRows = useMemo(() => rows.filter((row) => isGrowthOverviewRowExpandable(row)), [rows]);

    const resolvedLevel = responseLevel ?? GrowthOverviewLevel.SubIndustries;
    const nameColumnLabel = useMemo(() => {
        if (resolvedLevel === GrowthOverviewLevel.Domains) {
            const domainCount = responseTotalCount ?? rows.length;
            return translate(getGrowthOverviewColumnNameKey(domainCount, "domains"), { count: domainCount });
        }

        if (resolvedLevel === GrowthOverviewLevel.Industries) {
            const industryCount = responseTotalCount ?? rows.length;
            return translate(getGrowthOverviewColumnNameKey(industryCount, "industries"), { count: industryCount });
        }

        const domainCount =
            topLevelExpandableRows.length === 1
                ? (topLevelExpandableRows[0].totalDomainCount ??
                  topLevelExpandableRows[0].domainCount ??
                  topLevelExpandableRows[0].children?.length ??
                  0)
                : 0;

        if (topLevelExpandableRows.length === 1 && domainCount > 0) {
            return translate(getGrowthOverviewColumnNameKey(domainCount, "domains"), { count: domainCount });
        }

        return translate(getGrowthOverviewColumnNameKey(rows.length, "subIndustries"), { count: rows.length });
    }, [resolvedLevel, responseTotalCount, rows.length, topLevelExpandableRows]);

    const showTableLoader = rows.length === 0 && (isLoading || isFetching || isAwaitingRootData);
    const showError = !showTableLoader && isError && rows.length === 0;
    const showNoData = !showTableLoader && !isError && rows.length === 0;

    const periodCount = Math.max(periodHeaders.length, 1);

    useEffect(() => {
        const scrollArea = scrollAreaRef.current;
        if (!scrollArea || showTableLoader || showNoData) {
            return;
        }

        const scrollToNewestPeriod = () => {
            const maxScrollLeft = scrollArea.scrollWidth - scrollArea.clientWidth;
            if (maxScrollLeft > 0) {
                scrollArea.scrollLeft = maxScrollLeft;
            }
        };

        requestAnimationFrame(() => {
            scrollToNewestPeriod();
            requestAnimationFrame(scrollToNewestPeriod);
        });
    }, [periodHeaderKey, granularity, showTableLoader, showNoData]);

    useEffect(() => {
        const scrollArea = scrollAreaRef.current;
        const pending = pendingScrollRef.current;
        if (!scrollArea || !pending) {
            return;
        }

        if (isLoadingMoreIndustries || isLoadingMoreSubIndustries || loadingRowIds.size > 0) {
            return;
        }

        const rowId = getRevealedRowId(rawRows, pending);
        pendingScrollRef.current = null;
        if (!rowId) {
            return;
        }

        requestAnimationFrame(() => {
            scrollToRevealedRow(scrollArea, rowId);
        });
    }, [rawRows, isLoadingMoreIndustries, isLoadingMoreSubIndustries, loadingRowIds]);

    const summaryTitle = translate(getGrowthOverviewSummaryColumnKey(metric, granularity));

    const searchHighlightTerm = useMemo(() => {
        const trimmed = debouncedSearchTerm.trim();
        return trimmed.length > 0 ? trimmed : undefined;
    }, [debouncedSearchTerm]);

    const periodCellParams = useMemo(
        () => ({
            periodHeaders,
            metric,
            granularity,
            getHeatmapColor,
            getHeatmapStyle,
            searchHighlightTerm,
        }),
        [periodHeaders, metric, granularity, getHeatmapColor, getHeatmapStyle, searchHighlightTerm],
    );

    const remainingIndustryCount = Math.max((responseTotalCount ?? 0) - rawRows.length, 0);
    const showMoreIndustriesCells =
        responseLevel === GrowthOverviewLevel.Industries && remainingIndustryCount > 0
            ? renderShowAllDomainsRowCells({
                  rowId: "industries-show-more",
                  periodHeaders,
                  label: getGrowthOverviewShowMoreIndustriesLabel(remainingIndustryCount),
                  nestDepth: 0,
                  onShowAll: () => {
                      pendingScrollRef.current = { index: rawRows.length };
                      void loadMoreIndustries();
                  },
                  rowHover: getRowHoverProps("industries-show-more"),
              })
            : [];
    const remainingSubIndustryCount = Math.max((responseTotalCount ?? 0) - rawRows.length, 0);
    const showMoreSubIndustriesCells =
        responseLevel === GrowthOverviewLevel.SubIndustries && remainingSubIndustryCount > 0
            ? renderShowAllDomainsRowCells({
                  rowId: "sub-industries-show-more",
                  periodHeaders,
                  label: getGrowthOverviewShowMoreSubIndustriesLabel(remainingSubIndustryCount),
                  nestDepth: 0,
                  onShowAll: () => {
                      pendingScrollRef.current = { index: rawRows.length };
                      void loadMoreSubIndustries();
                  },
                  rowHover: getRowHoverProps("sub-industries-show-more"),
              })
            : [];

    return (
        <GrowthOverviewTableContainer data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-table`}>
            <GrowthOverviewTableTop
                subtitleFilters={subtitleFilters}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                totalCount={rows.length}
            />

            {showTableLoader ? (
                <TableLoader
                    rowNum={14}
                    Container={GrowthOverviewTableLoader}
                />
            ) : showError ? (
                <GrowthOverviewNoData>
                    <Text fontVariant="subtitle2">{translate(GROWTH_OVERVIEW_I18N_KEYS.error.title)}</Text>
                    <Text fontVariant="body2">{translate(GROWTH_OVERVIEW_I18N_KEYS.error.subtitle)}</Text>
                </GrowthOverviewNoData>
            ) : showNoData ? (
                <GrowthOverviewNoData>
                    <Text fontVariant="subtitle2">{translate(GROWTH_OVERVIEW_I18N_KEYS.noData.title)}</Text>
                    <Text fontVariant="body2">{translate(GROWTH_OVERVIEW_I18N_KEYS.noData.subtitle)}</Text>
                </GrowthOverviewNoData>
            ) : (
                <GrowthOverviewTableScrollWrapper onMouseLeave={handleRowMouseLeave}>
                    <GrowthOverviewTableScrollSteppers
                        scrollAreaRef={scrollAreaRef}
                        contentKey={periodHeaderKey}
                    />
                    <GrowthOverviewTableScrollArea
                        ref={scrollAreaRef}
                        data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-scroll-area`}
                    >
                        <GrowthOverviewHeatmapProvider getHeatmapStyle={getHeatmapStyle}>
                            <GrowthOverviewGrid
                                ref={gridRef}
                                $periodCount={periodCount}
                            >
                                <GrowthOverviewSortHeader
                                    label={nameColumnLabel}
                                    field={GROWTH_OVERVIEW_NAME_SORT_FIELD}
                                    sort={sort}
                                    onSort={handleSort}
                                    $stickyLeft={0}
                                />
                                <GrowthOverviewSortHeader
                                    label={summaryTitle}
                                    field={GROWTH_OVERVIEW_SUMMARY_SORT_FIELD}
                                    sort={sort}
                                    onSort={handleSort}
                                    $stickyLeft={NAME_WIDTH}
                                    $alignEnd
                                />
                                {periodHeaders.map((period) => (
                                    <GrowthOverviewSortHeader
                                        key={period.key}
                                        label={period.label}
                                        field={period.key}
                                        sort={sort}
                                        onSort={handleSort}
                                        isIncomplete={period.isIncomplete}
                                        granularity={granularity}
                                        $alignCenter
                                    />
                                ))}

                                {rows.flatMap((row) =>
                                    renderRowBranch({
                                        row,
                                        nestDepth: 0,
                                        expandedRowKeys,
                                        showAllDomainsRowKeys,
                                        loadingRowIds,
                                        onToggleRow: handleToggleRow,
                                        onShowAllDomains: handleShowAllDomains,
                                        onShowMoreSubIndustries: (rowId: string) => {
                                            const index = handleShowMoreSubIndustries(rowId);
                                            if (index !== undefined) {
                                                pendingScrollRef.current = { parentId: rowId, index };
                                            }
                                        },
                                        getRowHoverProps,
                                        periodCellParams,
                                    }),
                                )}
                                {showMoreIndustriesCells}
                                {showMoreSubIndustriesCells}
                                {isLoadingMoreIndustries
                                    ? renderBranchLoadingRowCells({
                                          rowId: "industries-loading-more",
                                          nestDepth: 0,
                                          periodHeaders,
                                      })
                                    : []}
                                {isLoadingMoreSubIndustries
                                    ? renderBranchLoadingRowCells({
                                          rowId: "sub-industries-loading-more",
                                          nestDepth: 0,
                                          periodHeaders,
                                      })
                                    : []}
                            </GrowthOverviewGrid>
                        </GrowthOverviewHeatmapProvider>
                    </GrowthOverviewTableScrollArea>
                </GrowthOverviewTableScrollWrapper>
            )}
        </GrowthOverviewTableContainer>
    );
};
